import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowRightLeft, BookOpen, ChevronRight, Download, Eye, FileSpreadsheet, FileDown, Filter, Heart, Lightbulb, LogOut, MessageSquare, Plus, Printer,
  RefreshCw, RotateCcw, Search, Star, Trash2, TrendingUp, TriangleAlert, UserRound,
} from 'lucide-react';
import * as hr from '../api';
import Loader from '../components/Loader';
import { useHR } from '../HRContext';
import Modal from '../components/Modal';
import { Select } from '../components/Controls';
import { download, toCSV } from '../format';
import { Chip } from './attendance/shared';
import { Table } from './missed/shared';
import EventForm from './workhistory/EventForm';
import { DOC_COLS, SECTIONS } from './workhistory/sections';
import {
  EVENT_TONE, LABEL_TONE, RISK_TONE, STATUS_TONE, TIMELINE_FILTERS, d2, d4, downloadXls, initials, my, naira, printDoc, sectionHtml, tone,
} from './workhistory/shared';

// Time & Attendance > Attendance Management > Working History (Stephanet's WorkingHistory.jsx): the Employee Working History &
// Lifecycle Center — employee list on the left, the selected employee's lifecycle on the right. The history is read live from the
// HR modules; the events HR records here are added with Add Event (a Pending one runs the Working History Event approval).

const CHIPS = ['All', 'Active', 'Terminated', 'Promoted', 'Transferred', 'On Probation', 'Warning / Query', 'Excellent Performer']
const CHIP_TEST = {
  All: () => true, Active: r => r.status === 'Active', Terminated: r => ['Terminated', 'Resigned'].includes(r.status), Promoted: r => r.promoted,
  Transferred: r => r.transferred, 'On Probation': r => r.probation, 'Warning / Query': r => r.warning, 'Excellent Performer': r => r.excellent,
}
const TABS = [['timeline', 'Timeline'], ['employment', 'Employment History'], ['promotions', 'Promotions & Transfers'], ['performance', 'Performance'],
  ['attendance', 'Attendance Summary'], ['leave', 'Leave & Overtime'], ['incidents', 'Incidents & Discipline'], ['training', 'Training & Certification'],
  ['benefits', 'Benefits'], ['assets', 'Assets / Tools / PPE'], ['payroll', 'Payroll & Salary'], ['documents', 'Documents'], ['exit', 'Exit / Termination'],
  ['notes', 'Management Notes'], ['audit', 'Audit Trail'], ['print', 'Print Profile']]
const QUICK = [
  ['Add Promotion', TrendingUp, { type: 'Promotion', kind: 'move' }], ['Add Transfer', ArrowRightLeft, { type: 'Transfer', kind: 'move' }],
  ['Add Performance', Star, { type: 'Performance Review', kind: 'perf', status: 'Completed' }], ['Add Training', BookOpen, { type: 'Training', kind: 'training', status: 'Completed' }],
  ['Add Incident', TriangleAlert, { type: 'Incident', kind: 'incident' }], ['Add Benefit', Heart, { type: 'Benefit Added', kind: 'benefit' }],
  ['Add Warning', TriangleAlert, { type: 'Warning', kind: 'incident' }], ['Add Exit', LogOut, { type: 'Resignation', kind: 'exit' }],
  ['Add Note', MessageSquare, { type: 'Management Note', kind: 'note', status: 'Completed' }],
]
const REPORT_PARTS = ['Employee Profile & Executive Summary', 'Timeline Events', 'Employment History', 'Promotions & Transfers', 'Performance History',
  'Attendance Summary', 'Leave & Overtime', 'Incidents & Discipline', 'Training & Certifications', 'Benefits History', 'Assets / Tools / PPE History',
  'Payroll & Salary History', 'Documents Summary', 'Exit / Termination Details', 'Management Notes', 'Audit Summary', 'Signature Lines', 'Confidentiality Footer']

export default function WorkingHistory() {
  const { notify, canManage } = useHR()
  const [list, setList] = useState(null)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [dept, setDept] = useState('')
  const [chip, setChip] = useState('All')
  const [showFilters, setShowFilters] = useState(false)
  const [sel, setSel] = useState(null)
  const [d, setD] = useState(null)
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState('timeline')
  const [full, setFull] = useState(false)
  const [form, setForm] = useState(null)
  const [busy, setBusy] = useState(false)
  const [viewing, setViewing] = useState(null)

  const loadList = () => hr.whEmployees().then(x => { setList(x); setError(''); return x }).catch(e => setError(e.message))
  const loadEmp = id => { setLoading(true); return hr.whEmployee(id).then(setD).catch(e => notify(e.message, 'bad')).finally(() => setLoading(false)) }
  useEffect(() => { loadList() }, []) // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (sel) { setD(null); loadEmp(sel) } }, [sel]) // eslint-disable-line react-hooks/exhaustive-deps

  const base = useMemo(() => {
    if (!list) return []
    const s = q.trim().toLowerCase()
    return list.rows.filter(r => (!dept || r.department === dept) && (!s || [r.name, r.employee_no].some(v => String(v || '').toLowerCase().includes(s))))
  }, [list, q, dept])
  const rows = base.filter(CHIP_TEST[chip])

  if (error && !list) return <div className="mdr"><div className="mdr-card mdr-empty">{error}</div></div>
  if (!list) return <Loader title="Loading working history…" subtitle="Fetching every employee" />
  const can = { ...list.can, create: list.can.create && canManage, delete: list.can.delete && canManage }

  const save = body => {
    setBusy(true)
    hr.whAddEvent(body)
      .then(r => { notify(r.message); setForm(null); return Promise.all([loadList(), loadEmp(sel)]) })
      .catch(e => notify(e.message, 'bad'))
      .finally(() => setBusy(false))
  }
  const remove = ev => {
    setBusy(true)
    hr.whDeleteEvent(ev.id)
      .then(r => { notify(r.message); setViewing(null); return Promise.all([loadList(), loadEmp(sel)]) })
      .catch(e => notify(e.message, 'bad'))
      .finally(() => setBusy(false))
  }
  const resubmit = ev => {
    setBusy(true)
    hr.whResubmit(ev.id)
      .then(r => { notify(r.message); setViewing(null); return loadEmp(sel) })
      .catch(e => notify(e.message, 'bad'))
      .finally(() => setBusy(false))
  }
  const printFull = () => {
    setFull(true)
    document.title = `${d.profile.employee_no} ${d.profile.name} - Working History`
    setTimeout(() => { window.print(); setFull(false) }, 350)
  }
  const exportXls = () => {
    const p = d.profile
    const parts = [sectionHtml('Employee Profile', ['Field', 'Value'], [
      ['Employee', `${p.name} (${p.employee_no})`], ['Designation', p.designation], ['Grade', p.grade], ['Department', p.department], ['Branch', p.branch],
      ['Site', p.site], ['Employment Type', p.employment_type], ['Hired', d4(p.hired)], ['Years of Service', p.years], ['Status', p.status],
      ['Supervisor', p.supervisor], ['HR Officer', p.hr_officer], ['Payroll', p.payroll_status], ['Rehire', p.rehire],
      ['Lifecycle Score', `${d.score} — ${d.label}`], ['Risk', p.risk]]),
    sectionHtml('Summary', ['Measure', 'Value', 'Note'], d.cards.map(c => [c.label, c.value, c.sub])),
    sectionHtml('HR Insights', ['Insight'], d.insights.map(i => [i])),
    sectionHtml('Timeline', ['Date', 'Type', 'Title', 'Details', 'By', 'Status', 'Source'], d.timeline.map(e => [d2(e.date), e.type, e.title, e.notes || '', e.by, e.status, e.source]))]
    for (const s of Object.values(SECTIONS)) parts.push(sectionHtml(s.title, s.cols.map(c => c[0]), s.rows(d).map(r => s.cols.map(c => c[1](r)))))
    parts.push(sectionHtml('Documents', DOC_COLS.map(c => c[0]), d.documents.map(r => DOC_COLS.map(c => c[1](r)))))
    for (const x of d.exits) parts.push(sectionHtml(`Exit — ${x.type}`, ['Item', 'Value'], [['Last Working Day', d4(x.last_day)], ...x.checks.map(c => [c.label, c.value]), ['Final HR Comment', x.hr_comment || ''], ['Final Management Comment', x.mgmt_comment || '']]))
    downloadXls(`${p.employee_no}-working-history.xls`, `Working History — ${p.name} (${p.employee_no})`, parts.join(''))
    notify('Full profile exported')
  }

  return (
    <div className={`mdr mds mdh wh ${full ? 'wh-full' : ''}`}>
      <div className="mdr-top no-print">
        <div>
          <h2>Employee Working History &amp; Lifecycle Center</h2>
          <p>Complete employee journey covering employment, performance, attendance, promotions, incidents, benefits, training, discipline, payroll impact, and exit history.</p>
        </div>
        <div className="mdr-top-actions"><button className="mdr-btn" onClick={() => Promise.all([loadList(), sel && loadEmp(sel)]).then(() => notify('Working history refreshed'))}><RefreshCw size={14} /> Refresh</button></div>
      </div>

      <div className="mdh-layout">
        <aside className="mdr-card mdh-list no-print">
          <div className="mdr-search"><Search size={14} /><input placeholder="Search name or ID..." value={q} onChange={e => setQ(e.target.value)} /></div>
          <button className={`wh-filter-toggle ${showFilters ? 'on' : ''}`} onClick={() => setShowFilters(s => !s)}><Filter size={13} /> {showFilters ? 'Hide Filters' : 'Show Filters'}</button>
          {showFilters && (
            <Select className="wh-dept" value={dept} onChange={e => setDept(e.target.value)}>
              <option value="">All Departments</option>{list.departments.map(x => <option key={x} value={x}>{x}</option>)}
            </Select>
          )}
          <div className="mdh-chips">
            {CHIPS.map(c => <button key={c} className={c === chip ? 'active' : ''} onClick={() => setChip(c)}>{c}<span>{base.filter(CHIP_TEST[c]).length}</span></button>)}
          </div>
          <div className="mdh-count wh-count-top">{rows.length} employee(s)</div>
          <div className="mdh-people">
            {rows.map((r, i) => (
              <button key={r.id} className={`mdh-person wh-person ${r.id === sel ? 'active' : ''}`} style={{ animationDelay: `${Math.min(i, 15) * 25}ms` }}
                onClick={() => { setSel(r.id); setTab('timeline'); setFull(false) }}>
                <span className="wh-avatar">{initials(r.name)}</span>
                <span className="wh-person-body">
                  <b>{r.name}</b>
                  <small>{[r.employee_no, r.grade, r.designation].filter(Boolean).join(' | ')}</small>
                  <small>{[r.department, r.branch].filter(Boolean).join(' | ')}</small>
                  <span className="mdh-tags">
                    <Chip tone={STATUS_TONE[r.status]}>{r.status}</Chip><Chip tone={RISK_TONE[r.risk]}>{`${r.risk} Risk`}</Chip>
                    <span className="wh-yrs">{r.years}yr</span>
                  </span>
                  {r.last_event && <small className="wh-last">Last: {r.last_event} · {my(r.last_date)}</small>}
                </span>
                <ChevronRight size={14} className="wh-chev" />
              </button>
            ))}
            {!rows.length && <div className="mdr-empty">No employees found.</div>}
          </div>
        </aside>

        <section className="mdh-detail">
          {!sel && (
            <div className="mdr-card wh-empty">
              <UserRound size={42} />
              <b>Select an employee to view their lifecycle history</b>
              <span>Use the panel on the left to search and select an employee.</span>
            </div>
          )}
          {sel && !d && (loading ? <Loader title="Loading lifecycle history…" subtitle="Reading the employee's records across HR" /> : <div className="mdr-card mdr-empty">No data.</div>)}
          {d && <Profile {...{ d, tab, setTab, full, can: { ...d.can, create: can.create, delete: can.delete }, busy, onAdd: preset => setForm(preset || {}), onView: setViewing, onPrint: printFull, onExport: exportXls }} />}
        </section>
      </div>

      {form && d && <EventForm profile={d.profile} types={list.event_types} choices={list.choices} lookups={list.lookups} preset={form.type ? form : null} busy={busy} onClose={() => setForm(null)} onSave={save} onError={m => notify(m, 'bad')} />}
      {viewing && <EventView ev={viewing} p={d.profile} can={can} busy={busy} onClose={() => setViewing(null)} onDelete={remove} onResubmit={resubmit} />}
    </div>
  )
}

function Profile({ d, tab, setTab, full, can, busy, onAdd, onView, onPrint, onExport }) {
  const p = d.profile
  const show = k => full || tab === k
  const csv = (key, s) => download(`${p.employee_no}-${key}.csv`, toCSV(s.cols.map(([label, get]) => ({ label, get })), s.rows(d)))
  const table = key => {
    const s = SECTIONS[key]
    return <Table head={s.cols.map(c => c[0])} rows={s.rows(d)} row={r => s.cols.map(c => (c[2] || c[1])(r))} empty={s.empty}
      onExport={can.export ? () => csv(key, s) : undefined} />
  }
  return (
    <div className="mdh-detail-inner">
      <div className="mdr-card wh-head">
        <div className="wh-head-main">
          <span className="wh-avatar lg">{initials(p.name)}</span>
          <div className="wh-head-text">
            <div className="wh-head-name"><h2>{p.name}</h2><Chip tone={STATUS_TONE[p.status]}>{p.status}</Chip><Chip tone={LABEL_TONE[d.label]}>{d.label}</Chip></div>
            <p>{[p.employee_no, p.designation, p.grade, p.employment_type].filter(Boolean).join(' · ')}</p>
            <p className="wh-meta">{[p.department, p.branch, p.site, p.hired && `Hired ${d4(p.hired)}`, `${p.years} yr(s) of service`].filter(Boolean).join('  ·  ')}</p>
            <p className="wh-meta">{[`Supervisor: ${p.supervisor || '—'}`, `HR Officer: ${p.hr_officer || '—'}`, `Payroll: ${p.payroll_status}`, `Rehire: ${p.rehire}`].join('  ·  ')}</p>
          </div>
          <div className={`wh-score t-${LABEL_TONE[d.label]}`}>
            <small>Lifecycle Score</small><b>{d.score}</b><span>{d.label}</span><em>Rehire: {p.rehire}</em>
            <i style={{ '--w': `${d.score}%` }} />
          </div>
        </div>
        <div className="wh-actions no-print">
          {can.create && <button className="mds-run sm" disabled={busy} onClick={() => onAdd(null)}><Plus size={13} /> Add Event</button>}
          {can.create && QUICK.map(([label, Ico, preset]) => <button key={label} className="mdr-btn" disabled={busy} onClick={() => onAdd(preset)}><Ico size={13} /> {label}</button>)}
          <span className="wh-spacer" />
          <button className="mdr-btn" onClick={onPrint}><Printer size={13} /> Print</button>
          {can.export && <button className="mdr-btn" onClick={onExport}><Download size={13} /> Export</button>}
        </div>
      </div>

      <div className="wh-cards">
        {d.cards.map((c, i) => (
          <div key={c.label} className="wh-card" style={{ animationDelay: `${i * 20}ms` }}>
            <b className={c.tone ? `t-${c.tone}` : ''}>{typeof c.value === 'number' ? Number(c.value.toFixed(1)).toLocaleString() : c.value}</b>
            <span>{c.label}</span><small>{c.sub}</small>
          </div>
        ))}
      </div>

      <div className="wh-insights">
        <h4><Lightbulb size={14} /> HR Insights</h4>
        {d.insights.map((x, i) => <p key={i}>• {x}</p>)}
      </div>

      {!full && (
        <div className="mds-tabs no-print" role="tablist">
          {TABS.map(([k, l]) => <button key={k} role="tab" className={k === tab ? 'active' : ''} aria-selected={k === tab} onClick={() => setTab(k)}>{l}</button>)}
        </div>
      )}

      {show('timeline') && <Section title="Timeline" full={full}><Timeline d={d} onView={onView} /></Section>}
      {['employment', 'promotions'].map(k => show(k) && <Section key={k} title={SECTIONS[k].title} full={full}>{table(k)}</Section>)}
      {show('performance') && (
        <Section title="Performance" full={full}>
          {d.performance.length > 0 && (
            <div className="wh-perf-kpis">
              {[['Total Reviews', d.performance_summary.total], ['Average Score', `${d.performance_summary.average}/100`], ['Last Rating', d.performance_summary.last],
                ['Best Score', `${d.performance_summary.best}/100`]].map(([l, v]) => <div key={l} className="mdr-card"><b>{v}</b><span>{l}</span></div>)}
            </div>
          )}
          {table('performance')}
        </Section>
      )}
      {show('attendance') && <Section title="Attendance Summary" full={full}>{table('attendance')}</Section>}
      {show('leave') && (
        <Section title="Leave & Overtime" full={full}>
          <h4 className="wh-sub">Leave History</h4>{table('leave')}
          <h4 className="wh-sub">Overtime</h4>{table('overtime')}
        </Section>
      )}
      {['incidents', 'training', 'benefits', 'assets', 'payroll'].map(k => show(k) && (
        <Section key={k} title={SECTIONS[k].title} full={full}>{d.not_available?.[k] && <p className="wh-note no-print">{d.not_available[k]}</p>}{table(k)}</Section>
      ))}
      {show('documents') && (
        <Section title="Documents" full={full}>
          <Table head={[...DOC_COLS.map(c => c[0]), 'Actions']} rows={d.documents} empty="No records found."
            onExport={can.export ? () => download(`${p.employee_no}-documents.csv`, toCSV(DOC_COLS.map(([label, get]) => ({ label, get })), d.documents)) : undefined}
            row={r => [r.type, <b key="t">{r.title}</b>, d2(r.uploaded), <span key="e" className={r.status === 'Expired' ? 'wh-neg' : ''}>{d2(r.expiry)}</span>,
              <Chip key="s" tone={tone(r.status, ['Active'], [], ['Expired'])}>{r.status}</Chip>, r.by, <small key="c">{r.confidentiality}</small>,
              <span key="a" className="wh-row-actions no-print">
                <button className="mds-view" onClick={() => onView({ doc: r })}><Eye size={12} /> View</button>
                <button className="mds-view" onClick={() => printDocRecord(r, p)}><Printer size={12} /> Print</button>
              </span>]} />
        </Section>
      )}
      {show('exit') && <Section title="Exit / Termination" full={full}><Exits d={d} /></Section>}
      {show('notes') && <Section title="Management Notes" full={full}>{table('notes')}</Section>}
      {show('audit') && <Section title="Audit Trail" full={full}>{table('audit')}</Section>}
      {!full && tab === 'print' && (
        <div className="mdr-card wh-print-card">
          <h4>Print Report includes:</h4>
          <div className="wh-print-list">{REPORT_PARTS.map(x => <span key={x}><b>✓</b> {x}</span>)}</div>
          <div className="wh-actions">
            <button className="mds-run" onClick={onPrint}><Printer size={14} /> Print Full Report</button>
            <button className="mdr-btn" onClick={onPrint} title="Choose 'Save as PDF' in the print window"><FileDown size={14} /> Export to PDF</button>
            {can.export && <button className="mdr-btn" onClick={onExport}><FileSpreadsheet size={14} /> Export to Excel</button>}
          </div>
        </div>
      )}
      {full && (
        <div className="wh-sign print-only-block">
          <div><span /><small>HR Manager — Name, Signature &amp; Date</small></div>
          <div><span /><small>Line Manager — Name, Signature &amp; Date</small></div>
          <p>CONFIDENTIAL — This report contains personal employee information. Generated {new Date().toLocaleString('en-GB')}.</p>
        </div>
      )}
    </div>
  )
}

const Section = ({ title, full, children }) => (full ? <div className="mdh-section"><h3 className="mdh-section-title">{title}</h3>{children}</div> : <>{children}</>)

function Timeline({ d, onView }) {
  const [f, setF] = useState('All')
  const evs = f === 'All' ? d.timeline : d.timeline.filter(e => (TIMELINE_FILTERS[f] || [f]).includes(e.type))
  return (
    <div className="mdr-card wh-tl">
      <div className="wh-tl-filters no-print">
        {['All', ...Object.keys(TIMELINE_FILTERS)].map(x => <button key={x} className={x === f ? 'active' : ''} onClick={() => setF(x)}>{x}</button>)}
        <span>{evs.length} events</span>
      </div>
      <div className="wh-tl-list">
        {evs.map((e, i) => (
          <div key={e.key} className="wh-tl-item" style={{ animationDelay: `${Math.min(i, 14) * 30}ms` }}>
            <div className={`wh-tl-dot t-${EVENT_TONE[e.type] || 'gray'}`}>{e.type.charAt(0)}</div>
            <div className="wh-tl-body">
              <div className="wh-tl-top">
                <Chip tone={EVENT_TONE[e.type]}>{e.type}</Chip><b>{e.title}</b><span className="wh-tl-date">{d4(e.date)}</span>
                <span className="wh-row-actions no-print">
                  <button className="mds-view" onClick={() => onView(e)}><Eye size={12} /> View</button>
                  <button className="mds-view" onClick={() => printEvent(e, d.profile)}><Printer size={12} /> Print</button>
                </span>
              </div>
              {e.notes && <p>{e.notes}</p>}
              {(e.from_grade || e.to_grade || e.move) && (
                <div className="wh-tl-move">
                  {e.from_grade && <span className="wh-grade">{e.from_grade}</span>}
                  {e.from_grade && e.to_grade && <ChevronRight size={12} />}
                  {e.to_grade && <span className="wh-grade new">{e.to_grade}</span>}
                  {e.move && <span>{e.move}</span>}
                </div>
              )}
              <small>By: {e.by || '—'} · Status: {e.status || '—'} · Source: {e.source}</small>
            </div>
          </div>
        ))}
        {!evs.length && <div className="mdr-empty">No events for selected filter.</div>}
      </div>
    </div>
  )
}

function Exits({ d }) {
  if (!d.exits.length) return <div className="mdr-card mdr-empty">No exit / termination records found.</div>
  const ok = ['Cleared', 'Completed', 'Yes', 'Approved', 'Eligible', 'Not Applicable', 'Paid in Lieu']
  const bad = ['Not Cleared', 'Rejected', 'Not Eligible', 'No', 'Declined']
  return d.exits.map((x, i) => (
    <div key={i} className="mdr-card wh-exit">
      <div className="wh-exit-head"><Chip tone={tone(x.type, ['Resignation', 'Retirement', 'End of Contract'], ['Redundancy', 'Mutual Separation', 'Exit'], ['Termination', 'Dismissal', 'Abscondment'])}>{x.type}</Chip>
        <b>Last Working Day: {d4(x.last_day)}</b></div>
      <div className="wh-exit-grid">
        {x.checks.map(c => <div key={c.label}><small>{c.label}</small><Chip tone={ok.includes(c.value) ? 'green' : bad.includes(c.value) ? 'red' : c.value === 'Not Recorded' ? 'gray' : 'yellow'}>{c.value}</Chip></div>)}
      </div>
      <div className="wh-exit-notes">
        <div><small>Final HR Comment</small><p>{x.hr_comment || '—'}</p></div>
        <div><small>Final Management Comment</small><p>{x.mgmt_comment || '—'}</p></div>
      </div>
    </div>
  ))
}

const LABELS = {
  salary_impact: 'Salary Impact', period: 'Review Period', reviewer: 'Reviewer', kpi: 'KPI', competency: 'Competency', attendance: 'Attendance', behaviour: 'Behaviour',
  technical: 'Technical', overall: 'Overall Score', rating: 'Rating', promotion_recommended: 'Promotion Recommended', training_recommended: 'Training Recommended',
  training_type: 'Training Type', provider: 'Provider', location: 'Location', cost: 'Cost', cert_issued: 'Certificate Issued', cert_no: 'Certificate No.',
  expiry: 'Certificate Expiry', renewal_required: 'Renewal Required', status: 'Status', impact: 'Impact', incident_type: 'Incident Type', severity: 'Severity',
  warning_issued: 'Warning Issued', query_issued: 'Query Issued', suspension: 'Suspension', benefit_type: 'Benefit Type', amount: 'Amount', frequency: 'Frequency',
  end_date: 'End Date', exit_type: 'Exit Type', last_working_date: 'Last Working Day', exit_interview: 'Exit Interview', notice_served: 'Notice Served',
  final_salary_paid: 'Final Salary Paid', asset_clearance: 'Asset Clearance', tool_clearance: 'Tool Clearance', ppe_clearance: 'PPE Clearance',
  payroll_clearance: 'Payroll Clearance', hr_clearance: 'HR Clearance', mgmt_approval: 'Management Approval', rehire_eligibility: 'Rehire Eligibility',
  final_hr_comment: 'Final HR Comment', final_mgmt_comment: 'Final Management Comment', note_type: 'Note Type', confidentiality: 'Confidentiality',
  visible_to: 'Visible To', action_required: 'Action Required', prev_salary: 'Previous Salary', new_salary: 'New Salary', allowance_change: 'Allowance Change',
  deduction_change: 'Deduction Change', item_name: 'Item', code: 'Code', serial: 'Serial No.', qty: 'Quantity', condition: 'Condition', expected_return: 'Expected Return',
}
const MONEY = ['salary_impact', 'cost', 'amount', 'prev_salary', 'new_salary']

function eventRows(e) {
  return [['Event Type', e.type], ['Title', e.title], ['Date', d4(e.date)], ['Description', e.notes], ['Previous Grade', e.from_grade], ['New Grade', e.to_grade],
    ['Change', e.move], ...Object.entries(e.details || {}).map(([k, v]) => [LABELS[k] || k, MONEY.includes(k) ? naira(Number(v)) : String(v)]),
    ['Payroll Impact', e.payroll_impact], ['Approved By', e.approved_by], ['Approval Status', e.status], ['Remarks', e.remarks], ['Recorded By', e.by], ['Source', e.source]]
}
const printEvent = (e, p) => printDoc(`${e.type}: ${e.title}`, eventRows(e), `${p.name} — ${p.employee_no} · ${p.designation || ''} · ${p.department || ''}`)
const docRows = r => [['Document', r.type], ['Document No.', r.title], ['Issuing Authority', r.authority], ['Issued', d4(r.issued)], ['Expiry', d4(r.expiry)],
  ['Status', r.status], ['Verified / Uploaded By', r.by], ['Confidentiality', r.confidentiality], ['Uploaded', d4(r.uploaded)], ['Remarks', r.remarks]]
const printDocRecord = (r, p) => printDoc(`${r.type} — ${r.title}`, docRows(r), `${p.name} — ${p.employee_no}`)

function EventView({ ev, p, can, busy, onClose, onDelete, onResubmit }) {
  const [confirm, setConfirm] = useState(false)
  const rows = (ev.doc ? docRows(ev.doc) : eventRows(ev)).filter(([, v]) => v !== null && v !== undefined && v !== '' && v !== '—')
  return (
    <Modal title={ev.doc ? `${ev.doc.type} — ${ev.doc.title}` : `${ev.type}: ${ev.title}`} onClose={onClose}>
      <p className="muted small">{p.name} — {p.employee_no}</p>
      <table className="wh-view"><tbody>{rows.map(([k, v]) => <tr key={k}><th>{k}</th><td>{v}</td></tr>)}</tbody></table>
      <div className="mdh-form-foot">
        {ev.editable && can.delete && (confirm
          ? <><span className="wh-confirm">Remove this event from the history?</span><button className="mdr-bulk-btn b-red" disabled={busy} onClick={() => onDelete(ev)}><Trash2 size={13} /> Yes, remove</button></>
          : <button className="mdr-btn" onClick={() => setConfirm(true)}><Trash2 size={13} /> Delete</button>)}
        {ev.can_resubmit && can.create && <button className="mdr-btn" disabled={busy} onClick={() => onResubmit(ev)}><RotateCcw size={13} /> Resubmit</button>}
        <span className="wh-spacer" />
        <button className="mdr-btn" onClick={() => (ev.doc ? printDocRecord(ev.doc, p) : printEvent(ev, p))}><Printer size={13} /> Print</button>
        <button className="mds-run" onClick={onClose}>Close</button>
      </div>
    </Modal>
  )
}
